/**
 * Accessible criteria funnel + recovery ranking (CSS bars, no chart lib).
 */
export default function MatchCriteriaFunnel({ criteria = [], actualMatchCount = 0, baselineJobCount = 0 }) {
  const items = Array.isArray(criteria) ? criteria : [];
  const maxBar = Math.max(baselineJobCount, actualMatchCount, 1, ...items.map((c) => c.StandalonePassCount ?? c.standalonePassCount ?? 0));

  if (!items.length) {
    return <p className="byline">Criteria impact will appear after your first match evaluation.</p>;
  }

  const ranked = [...items].sort(
    (a, b) => (b.RecoveredJobCount ?? b.recoveredJobCount ?? 0) - (a.RecoveredJobCount ?? a.recoveredJobCount ?? 0)
  );

  return (
    <div className="match-criteria-funnel">
      <p className="byline">
        Baseline openings considered: {baselineJobCount}. Current matches: {actualMatchCount}.
      </p>

      <h3 className="match-criteria-funnel__subhead">How criteria combine</h3>
      <table className="match-criteria-funnel__table">
        <caption className="sr-only">Cumulative match funnel by criterion</caption>
        <thead>
          <tr>
            <th scope="col">Criterion</th>
            <th scope="col">Your setting</th>
            <th scope="col">Standalone</th>
            <th scope="col">Cumulative left</th>
            <th scope="col">Coverage</th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const key = c.Key || c.key;
            const standalone = c.StandalonePassCount ?? c.standalonePassCount ?? 0;
            const cumulative = c.CumulativeRemainingCount ?? c.cumulativeRemainingCount ?? 0;
            const pct = Math.round((standalone / maxBar) * 100);
            return (
              <tr key={key}>
                <th scope="row">{c.Label || c.label}</th>
                <td>{c.ConfiguredValue || c.configuredValue || "—"}</td>
                <td>{standalone}</td>
                <td>{cumulative}</td>
                <td>
                  <div className="match-criteria-funnel__bar" role="img" aria-label={`${pct}% of baseline`}>
                    <span style={{ width: `${pct}%` }} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <h3 className="match-criteria-funnel__subhead">Biggest opportunities to improve matches</h3>
      <ol className="match-criteria-funnel__ranked">
        {ranked
          .filter((c) => (c.RecoveredJobCount ?? c.recoveredJobCount ?? 0) > 0)
          .slice(0, 5)
          .map((c) => (
            <li key={c.Key || c.key}>
              <strong>{c.Label || c.label}</strong>
              {" — "}
              could add ~{c.RecoveredJobCount ?? c.recoveredJobCount} matches
              {c.Guidance || c.guidance ? <div className="byline">{c.Guidance || c.guidance}</div> : null}
            </li>
          ))}
      </ol>
      {!ranked.some((c) => (c.RecoveredJobCount ?? c.recoveredJobCount ?? 0) > 0) ? (
        <p className="byline">No single criterion is blocking a large set of openings right now.</p>
      ) : null}

      {items
        .filter((c) => (c.CompensationLadder || c.compensationLadder || []).length > 0)
        .map((c) => {
          const ladder = c.CompensationLadder || c.compensationLadder || [];
          return (
            <div key={`ladder-${c.Key || c.key}`} className="match-criteria-funnel__ladder">
              <h3 className="match-criteria-funnel__subhead">
                Pay what-if: {c.Label || c.label}
              </h3>
              <ul>
                {ladder.map((step) => (
                  <li key={step.Label || step.label || step.Amount}>
                    {step.Label || step.label}: {step.ProjectedMatchCount ?? step.projectedMatchCount} matches
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
    </div>
  );
}
